import { t } from "i18next"
import debounce from "lodash/debounce"

import { DisplayableError } from "@treefort/lib/displayable-error"

import analytics from "./analytics"
import { audioPlayer } from "./audio-player"
import { EbookConsumableContent } from "./consumable-content"
import DownloadItem from "./download-item"
import {
  EbookReader,
  ebookReader,
  EbookReaderLocation,
  EbookReaderEvent,
  EbookReaderTheme,
  DEFAULT_EBOOK_READER_THEME,
} from "./ebook-reader"
import { logError } from "./logging"
import { getNetworkState, isOfflineState } from "./network-state"
import {
  EbookProgressItem,
  getProgressItemKeyFromConsumableContent,
  fetchProgressForConsumableContent,
  persistProgress,
} from "./progress-item"
import { Store } from "./store"

const themeStore = new Store({ key: "contentEbookThemes" })

// Debounce the handling of theme changes aggressively. This is not
// time-sensitive and there's no need to pummel the local store if the user does
// something like mash the increase font size button.
const handleThemeChange = debounce(
  async (theme: EbookReaderTheme) => {
    // Save the theme for the current ebook
    const ebook = ebookReader.getEbook()
    if (ebook?.extra) {
      const key = getProgressItemKeyFromConsumableContent(
        ebook.extra.consumableContent,
      )
      themeStore.set(key, theme)
    }

    // Save the theme as the new default
    themeStore.set("default", theme)
  },
  1000,
  { leading: true, trailing: true },
)

function addListeners(ebookReader: EbookReader): void {
  let engagementTimeStart: number | null = null

  // Pause the audio player when a new book is loaded
  ebookReader.on(EbookReaderEvent.EbookChanged, async (ebook) => {
    engagementTimeStart = null
    if (ebook) {
      audioPlayer.pause()

      // Log a read request but don't block playback while we do so
      const { consumableContent, profileId } = ebook.extra
      void analytics.logReadRequest({
        consumableContent,
        progressItem: await fetchProgressForConsumableContent({
          consumableContent,
          profileId,
        }),
      })
    }
  })

  // Save progress and log analytics on location change
  ebookReader.on(
    EbookReaderEvent.LocationChanged,
    async ({ previous, current }) => {
      const ebook = ebookReader.getEbook()

      // Bail if we couldn't fetch the ebook (should never happen)
      if (!ebook) {
        engagementTimeStart = null
        return
      }

      const { consumableContent, profileId } = ebook.extra

      const previousProgressItem = await fetchProgressForConsumableContent({
        consumableContent,
        profileId,
      })

      // Save progress for the current location
      if (current?.type === "rendered") {
        persistProgress(
          new EbookProgressItem({
            data: {
              contentId: consumableContent.content.id,
              contentType: consumableContent.content.type,
              progress: {
                percent:
                  current.metadata.percent ??
                  previousProgressItem.getProgress().percent,
                finished:
                  current.metadata.endOfBookVisible ??
                  previousProgressItem.getProgress().finished,
              },
              location: current,
            },
            profileId,
          }),
        )
      }

      // Log analytics for the previous location (now that we can calculate
      // engagement time for that location)
      if (engagementTimeStart && previous?.type === "rendered") {
        analytics.logReadProgress({
          progressItem: previousProgressItem,
          consumableContent: ebook.extra.consumableContent,
          engagementTime: performance.now() - engagementTimeStart,
          wordsVisible: previous.metadata.wordsVisible,
        })
      }

      // Reset the engagement start time each time we render a new location
      if (current?.type === "rendered") {
        engagementTimeStart = performance.now()
      }
    },
  )

  // Log analytics for the last page when closing the book
  ebookReader.on(EbookReaderEvent.EbookWillChange, async (nextEbook) => {
    const ebook = ebookReader.getEbook()
    const location = ebookReader.getLocation()

    // Bail if we couldn't fetch the ebook (should never happen)
    if (!ebook) {
      engagementTimeStart = null
      return
    }

    const { profileId, consumableContent } = ebook.extra
    const progressItem = await fetchProgressForConsumableContent({
      consumableContent,
      profileId,
    })
    if (!nextEbook && engagementTimeStart && location?.type === "rendered") {
      analytics.logReadProgress({
        progressItem,
        consumableContent: ebook.extra.consumableContent,
        engagementTime: performance.now() - engagementTimeStart,
        wordsVisible: location.metadata.wordsVisible,
      })
    }
  })

  // Save theme selection
  ebookReader.on(EbookReaderEvent.ThemeChanged, handleThemeChange)
}

export async function readContentEbook({
  consumableContent,
  profileId,
  location,
}: {
  consumableContent: EbookConsumableContent
  profileId: string | null
  location?: EbookReaderLocation
}): Promise<void> {
  try {
    // Make sure we're either online or the ebook is downloaded
    const downloadItem = new DownloadItem({ consumableContent })
    const [networkState, downloadItemState] = await Promise.all([
      getNetworkState(),
      downloadItem.getState(),
    ])
    if (
      isOfflineState(networkState) &&
      downloadItemState.type !== "downloaded"
    ) {
      throw new DisplayableError(
        t("Cannot read ebook - no network connection."),
      )
    }

    // Make sure we were given an ebook to read
    const { content } = await downloadItem.getOfflineConsumableContent()

    if (content.details.ebookMedia?.status !== "available") {
      throw new Error(
        `Attempted to read ebook "${consumableContent.content.id}" without any media.`,
      )
    }

    // Load any saved theme settings
    const key = getProgressItemKeyFromConsumableContent(consumableContent)
    const [theme, defaultTheme] = await Promise.all([
      themeStore.get<EbookReaderTheme>(key),
      themeStore.get<EbookReaderTheme>("default"),
    ])

    // Start reading
    ebookReader.setEbook(
      {
        media: content.details.ebookMedia.data,
        title: content.title,
        extra: { consumableContent, profileId },
      },
      {
        location:
          location ??
          (
            await fetchProgressForConsumableContent({
              consumableContent,
              profileId,
            })
          ).getLocation(),
        theme: {
          ...DEFAULT_EBOOK_READER_THEME,
          ...defaultTheme,
          ...theme,
          invertImagesInDarkMode: Boolean(
            content.details.invertImagesInDarkMode,
          ),
        },
      },
    )
  } catch (error) {
    logError(
      error instanceof DisplayableError
        ? error
        : new DisplayableError(
            t("An error occurred. Please contact us if the issue persists."),
            error,
          ),
    )
  }
}

addListeners(ebookReader)
